import React, { useState, useEffect, useRef } from 'react';
import { Toggle } from '../../../components/ui/Toggle';
import Icon from '../../../components/AppIcon';

const NotificationSection = ({ settings, onUpdateNotifications }) => {
  const [localSettings, setLocalSettings] = useState({
    emailNotifications: {
      newsletter: settings?.emailNotifications?.newsletter ?? false
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Sync state with props only when not saving
  useEffect(() => {
    if (settings && !isSaving) {
      setLocalSettings({
        emailNotifications: {
          newsletter: settings?.emailNotifications?.newsletter ?? false
        }
      });
    }
  }, [settings, isSaving]);

  // Auto-save function with debounce
  const autoSaveSettings = async (updatedSettings) => {
    // Clear any existing timeout
    if (saveTimeoutRef?.current) {
      clearTimeout(saveTimeoutRef?.current);
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      setSaveError(null);
      try {
        await onUpdateNotifications(updatedSettings);
      } catch (error) {
        console.error('Failed to auto-save notification settings:', error);
        setSaveError(error?.message || 'Failed to save settings');
        // Revert to previous state on error
        setLocalSettings({
          emailNotifications: {
            newsletter: settings?.emailNotifications?.newsletter ?? false
          }
        });
      } finally {
        setIsSaving(false);
      }
    }, 500); // 500ms debounce
  };

  const handleSettingChange = (value) => {
    const updatedSettings = {
      emailNotifications: {
        newsletter: value
      }
    };
    
    setLocalSettings(updatedSettings);
    autoSaveSettings(updatedSettings);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef?.current) {
        clearTimeout(saveTimeoutRef?.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header without Save Button */}
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-organic border border-border">
        <div>
          <h3 className="font-body font-body-medium text-sm text-card-foreground">Notification Settings</h3>
          <p className="font-caption text-xs text-muted-foreground mt-1">
            {isSaving ? 'Saving...' : 'Your settings are saved automatically'}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {saveError && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-organic text-destructive">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} />
            <span className="text-sm">{saveError}</span>
          </div>
        </div>
      )}

      <div className="bg-card rounded-organic border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Icon name="Mail" size={20} color="white" />
          </div>
          <div>
            <h3 className="font-heading font-heading-bold text-lg text-card-foreground">Email Notifications</h3>
            <p className="font-caption text-sm text-muted-foreground">Manage email communications and updates</p>
          </div>
        </div>

        <div className="space-y-4">
          <Toggle
            checked={localSettings?.emailNotifications?.newsletter}
            onChange={(e) => handleSettingChange(e?.target?.checked)}
            label="Newsletter"
            description="Receive updates about parks, trips, and more!"
            disabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationSection;